import React, { createContext, useEffect, useReducer } from "react";
import type { ReactNode } from "react";
import type { CartItem, Product } from "../types";

interface CartState {
  items: CartItem[];
  total: number;
}

type CartAction =
  | { type: "ADD_ITEM"; product: Product }
  | { type: "REMOVE_ITEM"; productId: number }
  | { type: "UPDATE_QUANTITY"; productId: number; quantity: number }
  | { type: "CLEAR_CART" };

interface CartContextType {
  state: CartState;
  addItem: (product: Product) => void;
  removeItem: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearCart: () => void;
  getItemsCount: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export { CartContext };

const calculateTotal = (items: CartItem[]): number => {
  return items.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
};

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case "ADD_ITEM": {
      const existingItem = state.items.find(
        (item) => item.product.id === action.product.id
      );

      if (existingItem) {
        const updatedItems = state.items.map((item) =>
          item.product.id === action.product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
        return {
          items: updatedItems,
          total: calculateTotal(updatedItems),
        };
      } else {
        const updatedItems = [
          ...state.items,
          { product: action.product, quantity: 1 },
        ];
        return {
          items: updatedItems,
          total: calculateTotal(updatedItems),
        };
      }
    }

    case "REMOVE_ITEM": {
      const updatedItems = state.items.filter(
        (item) => item.product.id !== action.productId
      );
      return {
        items: updatedItems,
        total: calculateTotal(updatedItems),
      };
    }

    case "UPDATE_QUANTITY": {
      if (action.quantity <= 0) {
        const updatedItems = state.items.filter(
          (item) => item.product.id !== action.productId
        );
        return {
          items: updatedItems,
          total: calculateTotal(updatedItems),
        };
      }

      const updatedItems = state.items.map((item) =>
        item.product.id === action.productId
          ? { ...item, quantity: action.quantity }
          : item
      );
      return {
        items: updatedItems,
        total: calculateTotal(updatedItems),
      };
    }

    case "CLEAR_CART": {
      return {
        items: [],
        total: 0,
      };
    }

    default:
      return state;
  }
};

const CART_STORAGE_KEY = "shopdemo_cart";

// Checkout is a full navigation, not a client-side route, so in-memory cart
// state would be lost on the way. Persisting is also just realistic for a shop.
const EMPTY_CART: CartState = { items: [], total: 0 };

const loadInitialState = (): CartState => {
  try {
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    if (!saved) return EMPTY_CART;
    const parsed = JSON.parse(saved);
    // Shape check, not just a parse guard: valid JSON of the wrong shape
    // ("null", a string, an object without items) would otherwise reach the
    // renderer and blow up on state.items.length.
    if (!parsed || !Array.isArray(parsed.items)) return EMPTY_CART;
    return { items: parsed.items, total: calculateTotal(parsed.items) };
  } catch {
    return EMPTY_CART;
  }
};

export const CartProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(
    cartReducer,
    undefined,
    loadInitialState
  );

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const addItem = (product: Product) => {
    dispatch({ type: "ADD_ITEM", product });
  };

  const removeItem = (productId: number) => {
    dispatch({ type: "REMOVE_ITEM", productId });
  };

  const updateQuantity = (productId: number, quantity: number) => {
    dispatch({ type: "UPDATE_QUANTITY", productId, quantity });
  };

  const clearCart = () => {
    dispatch({ type: "CLEAR_CART" });
  };

  const getItemsCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        state,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        getItemsCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
